// src/utils/invoicing/pdfRenderer.js
//
// PDF template layer.
// Accepts a seller (orgInfo) and an InvoiceData object from invoiceBuilder.
// Knows nothing about deliveries, members, or grouping.

import jsPDF from 'jspdf';

function fmt(ms) {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('fi-FI', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function fmtEur(n) {
  return `${Number(n || 0).toFixed(2)} €`;
}

const M    = 14;   // page margin
const COLS = [46, 24, 50, 50, 22];
const COL_TOTAL_W = COLS.reduce((a, b) => a + b, 0);
const ROW_H = 8;

// ─── Page header ──────────────────────────────────────────────────────────────

function renderHeader(pdf, title, invoiceData, seller={}) {
  console.log('seller', seller)
  const pageW = pdf.internal.pageSize.getWidth();

  // Right: title + meta
  pdf.setFontSize(22);
  pdf.setFont('helvetica', 'bold');
  pdf.text(title, pageW - M, 20, { align: 'right' });

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Laskunumero: ${invoiceData.invoiceNumber}`, pageW - M, 28, { align: 'right' });
  pdf.text(`Päivämäärä:  ${fmt(invoiceData.issuedAt)}`, pageW - M, 34, { align: 'right' });
  pdf.text(`Eräpäivä:    ${fmt(invoiceData.dueAt)}`,    pageW - M, 40, { align: 'right' });

  // Left: seller
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.text(seller.companyName || '—', M, 20);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  let sy = 27;
  [
    seller.yNumber ? `Y-tunnus: ${seller.yNumber}` : null,
    seller.iban    ? `IBAN: ${seller.iban}`        : null,
    seller.address || null,
  ].filter(Boolean).forEach((line) => { pdf.text(line, M, sy); sy += 6; });

  // Buyer block
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.text('Laskutettava', M, 56);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  let ry = 63;
  const b = invoiceData.buyer;
  [
    b.companyName || null,
    b.yNumber    ? `Y-tunnus: ${b.yNumber}`  : null,
    b.reference  ? `Viite: ${b.reference}`   : null,
    b.department ? `Osasto: ${b.department}` : null,
  ].filter(Boolean).forEach((line) => { pdf.text(line, M, ry); ry += 6; });

  pdf.setDrawColor(200, 200, 200);
  pdf.line(M, 78, pageW - M, 78);

  return 84;
}

// ─── Delivery rows ─────────────────────────────────────────────────────────────

function renderRows(pdf, rows, startY) {
  const pageH = pdf.internal.pageSize.getHeight();
  let y = startY;

  // Column headers
  pdf.setFillColor(37, 99, 235);
  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  let x = M;
  ['Toimitusnro', 'Pvm', 'Lähtöpaikka', 'Määränpää', 'Summa €'].forEach((h, i) => {
    pdf.rect(x, y, COLS[i], ROW_H, 'F');
    pdf.text(h, x + 2, y + 5.5);
    x += COLS[i];
  });
  y += ROW_H;

  pdf.setTextColor(0, 0, 0);
  pdf.setFont('helvetica', 'normal');

  rows.forEach((r, idx) => {
    if (y + ROW_H > pageH - M) { pdf.addPage(); y = 20; }
    if (idx % 2 === 0) {
      pdf.setFillColor(248, 250, 252);
      pdf.rect(M, y, COL_TOTAL_W, ROW_H, 'F');
    }
    const cells = [
      String(r.id || '').substring(0, 18),
      fmt(r.date),
      String(r.origin      || '—').substring(0, 22),
      String(r.destination || '—').substring(0, 22),
      r.unitPrice.toFixed(2),
    ];
    x = M;
    pdf.setFontSize(8);
    cells.forEach((text, i) => {
      pdf.text(text, x + 2, y + 5.5);
      x += COLS[i];
    });
    y += ROW_H;
  });

  return y;
}

// ─── Billing fee row ──────────────────────────────────────────────────────────

function renderBillingFeeRow(pdf, billingFee, startY) {
  if (!billingFee) return startY;
  const pageH = pdf.internal.pageSize.getHeight();
  let y = startY;
  if (y + ROW_H > pageH - M) { pdf.addPage(); y = 20; }

  pdf.setFillColor(255, 251, 235);
  pdf.rect(M, y, COL_TOTAL_W, ROW_H, 'F');
  pdf.setTextColor(0, 0, 0);
  pdf.setFont('helvetica', 'italic');
  pdf.setFontSize(8);

  const labelW = COLS.slice(0, 4).reduce((a, b) => a + b, 0);
  pdf.text('Laskutuslisä', M + 2, y + 5.5);
  pdf.text(Number(billingFee).toFixed(2), M + labelW + 2, y + 5.5);
  pdf.setFont('helvetica', 'normal');

  return y + ROW_H;
}

// ─── Totals block ─────────────────────────────────────────────────────────────

function renderTotals(pdf, invoiceData, startY) {
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  let y = startY;
  if (y + 44 > pageH - M) { pdf.addPage(); y = 20; }

  y += 8;
  pdf.setDrawColor(200, 200, 200);
  pdf.line(M, y, pageW - M, y);
  y += 8;

  const labelX = pageW - 80;
  const valueX = pageW - M;

  const row = (label, value, bold = false) => {
    pdf.setFont('helvetica', bold ? 'bold' : 'normal');
    pdf.setFontSize(9);
    pdf.text(label, labelX, y);
    pdf.text(value, valueX, y, { align: 'right' });
    y += 7;
  };

  if (invoiceData.alvRate > 0) {
    row('Veroton yhteensä:',    fmtEur(invoiceData.net));
    row(`ALV ${invoiceData.alvRate} %:`, fmtEur(invoiceData.alvAmount));
    pdf.setDrawColor(200, 200, 200);
    pdf.line(labelX, y - 2, pageW - M, y - 2);
    row('Yhteensä (sis. ALV):', fmtEur(invoiceData.gross), true);
  } else {
    row('Maksettava yhteensä:', fmtEur(invoiceData.net), true);
  }
}

// ─── Public renderers ─────────────────────────────────────────────────────────

/**
 * Renders an outgoing invoice (org → sender) to PDF and triggers download.
 * @param {object} seller      - orgInfo / paymentInfo
 * @param {object} invoiceData - from buildOutgoingGroups
 */
export function renderOutgoingPdf(seller, invoiceData) {
  const pdf = new jsPDF();
  const y0  = renderHeader(pdf, 'LASKU', invoiceData, seller);
  const y1  = renderRows(pdf, invoiceData.rows, y0);
  const y2  = renderBillingFeeRow(pdf, invoiceData.billingFee, y1);
  renderTotals(pdf, invoiceData, y2);
  pdf.save(`lasku-${invoiceData.invoiceNumber}.pdf`);
}

/**
 * Renders a purchase invoice (org → courier) to PDF and triggers download.
 * @param {object} orgInfo     - org billing info (shown as the paying party)
 * @param {object} invoiceData - from buildPurchaseGroups
 */
export function renderPurchasePdf(orgInfo, invoiceData) {
  const pdf = new jsPDF();

  // For purchase invoices the header labels flip:
  // seller slot = courier (payee), buyer slot = org (payer)
  const y0 = renderHeader(pdf, 'OSTOLASKU', invoiceData, invoiceData.buyer);

  // Override the buyer block label text by re-rendering over it isn't clean,
  // so we pass orgInfo as seller and swap the buyer to the courier in the header call.
  // Instead, use a custom header for purchase invoices:
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();

  const newPdf = new jsPDF();

  newPdf.setFontSize(22);
  newPdf.setFont('helvetica', 'bold');
  newPdf.text('OSTOLASKU', pageW - M, 20, { align: 'right' });

  newPdf.setFontSize(9);
  newPdf.setFont('helvetica', 'normal');
  newPdf.text(`Laskunumero: ${invoiceData.invoiceNumber}`, pageW - M, 28, { align: 'right' });
  newPdf.text(`Päivämäärä:  ${fmt(invoiceData.issuedAt)}`, pageW - M, 34, { align: 'right' });
  newPdf.text(`Eräpäivä:    ${fmt(invoiceData.dueAt)}`,    pageW - M, 40, { align: 'right' });

  // Left: courier (the one being paid)
  const c = invoiceData.buyer;
  newPdf.setFont('helvetica', 'bold'); newPdf.setFontSize(10);
  newPdf.text('Laskuttaja (kuriiri)', M, 20);
  newPdf.setFont('helvetica', 'normal'); newPdf.setFontSize(9);
  let sy = 27;
  [
    c.companyName || null,
    c.yNumber ? `Y-tunnus: ${c.yNumber}` : null,
    c.iban    ? `IBAN: ${c.iban}`        : null,
  ].filter(Boolean).forEach((l) => { newPdf.text(l, M, sy); sy += 6; });

  // Buyer: org as the paying party
  newPdf.setFont('helvetica', 'bold'); newPdf.setFontSize(10);
  newPdf.text('Maksaja', M, 56);
  newPdf.setFont('helvetica', 'normal'); newPdf.setFontSize(9);
  let ry = 63;
  [
    orgInfo?.companyName || null,
    orgInfo?.yNumber ? `Y-tunnus: ${orgInfo.yNumber}` : null,
  ].filter(Boolean).forEach((l) => { newPdf.text(l, M, ry); ry += 6; });

  newPdf.setDrawColor(200, 200, 200);
  newPdf.line(M, 78, pageW - M, 78);

  const y1 = renderRows(newPdf, invoiceData.rows, 84);
  renderTotals(newPdf, invoiceData, y1);
  newPdf.save(`ostolasku-${invoiceData.invoiceNumber}.pdf`);
}
